// react-map-gl doesn't re-export the maplibre Map type cleanly; use the minimal
// surface we actually call so we stay decoupled from the import path.
interface MapGl {
  project(lngLat: [number, number]): { x: number; y: number };
  getContainer(): HTMLElement;
}

/** Anchor = the point ON the popup pinned to the coordinate, so the popup grows
 *  away from it: `bottom` → popup sits above the pin, `top` → below, etc. */
export type PopupAnchor =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

/** Rough rendered footprint of a popup, used only to reserve space when deciding
 *  which way to open. Over-estimate slightly so we flip before clipping. */
export interface PopupSize {
  width: number;
  height: number;
}

// Keep the popup tip a little off the map edges even after we flip it.
const EDGE_MARGIN = 12;

/**
 * Choose the anchor that keeps a popup inside the map viewport.
 *
 * We project the marker to screen space and check how much room is left on each
 * side. The visual default is to open *above* the pin (anchor `bottom`); we flip
 * to below when the pin is near the top, and bias left/right when it's near a
 * side edge — so a marker tucked into any corner still opens fully on-screen.
 */
export function choosePopupAnchor(
  map: MapGl | undefined,
  lng: number,
  lat: number,
  size: PopupSize,
  fallback: PopupAnchor = 'bottom',
): PopupAnchor {
  if (!map) return fallback;

  const { x, y } = map.project([lng, lat]);
  const { clientWidth: w, clientHeight: h } = map.getContainer();
  if (!w || !h) return fallback;

  // Vertical: prefer opening above the pin; flip below only when there isn't
  // room above (and there is below).
  const roomAbove = y - EDGE_MARGIN;
  const roomBelow = h - y - EDGE_MARGIN;
  const vertical: 'top' | 'bottom' =
    roomAbove < size.height && roomBelow > roomAbove ? 'top' : 'bottom';

  // Horizontal: a centered popup overhangs by half its width on each side. Bias
  // toward the side with room when the pin sits near a left/right edge.
  const half = size.width / 2;
  let horizontal: '' | 'left' | 'right' = '';
  if (x - half < EDGE_MARGIN) horizontal = 'left'; // overflow left → grow right
  else if (x + half > w - EDGE_MARGIN) horizontal = 'right'; // overflow right → grow left

  return (horizontal ? `${vertical}-${horizontal}` : vertical) as PopupAnchor;
}
